/**
 * NCR Create Page - Create a new Non-Conformance Report
 * Based on Portal v1.89 NCR implementation
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { qualityAPI, suppliersAPI, purchasingAPI, coreAPI, platformSettingsAPI, fetchAllPages } from '../lib/api';
import type { SourceOfRejection, FaultCodeGroup, FaultCodeCategory, UserProfile, QualityTabsVisibility } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useFormatters } from '../contexts/PreferencesContext';
import { useView } from '../contexts/ViewContext';
import { toast } from 'sonner';
import NCRDetailsForm, { type NCRFormData, type RepeatInfo } from '../components/quality/ncr-create/NCRDetailsForm';
import NCRGrnLinkDialog from '../components/quality/ncr-create/NCRGrnLinkDialog';
import { extractNcrError } from './ncr/ncrCreateErrors';
import { displayPrice } from '../lib/unitPrice';

export default function NCRCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Admin toggle: when on, Fault Group/Category are optional on this form.
  const { ncrFaultClassificationOptional } = useMenuSettings();
  // CR-4707 precision, same source the PO Line pop-up reads.
  const { preferences } = useFormatters();
  const priceDecimals = preferences.unit_price_decimals;
  // Group/company/site the user is currently viewing — the GRN lookup is
  // scoped to it so a number that exists on another site is not linkable here.
  const { getViewQueryParams } = useView();
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [grnLinked, setGrnLinked] = useState(false);
  const [customerUsers, setCustomerUsers] = useState<UserProfile[]>([]);

  // Tab visibility check
  const [tabVisibility, setTabVisibility] = useState<QualityTabsVisibility>({
    ncrs: true, concessions: true, approval_register: true,
  });
  const [tabSettingsLoaded, setTabSettingsLoaded] = useState(false);

  // Check if NCRs tab is enabled
  useEffect(() => {
    platformSettingsAPI.getDisplaySettings()
      .then(res => {
        if (res.data?.quality_tabs) {
          setTabVisibility(prev => ({ ...prev, ...res.data.quality_tabs }));
        }
      })
      .catch(() => { /* use defaults */ })
      .finally(() => setTabSettingsLoaded(true));
  }, []);

  // Redirect if NCRs tab is disabled
  useEffect(() => {
    if (!tabSettingsLoaded) return;
    if (!tabVisibility.ncrs) {
      navigate('/quality', { replace: true });
    }
  }, [tabSettingsLoaded, tabVisibility.ncrs, navigate]);

  // Classification dropdown options
  const [sourcesOfRejection, setSourcesOfRejection] = useState<SourceOfRejection[]>([]);
  const [faultGroups, setFaultGroups] = useState<FaultCodeGroup[]>([]);
  const [faultCategories, setFaultCategories] = useState<FaultCodeCategory[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<FaultCodeCategory[]>([]);
  // CR-6387: per-field errors returned by the backend, surfaced inline.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // GRN dialog state
  const [showGrnDialog, setShowGrnDialog] = useState(true);
  const [grnInput, setGrnInput] = useState('');
  const [lookingUpGRN, setLookingUpGRN] = useState(false);

  const [formData, setFormData] = useState<NCRFormData>({
    supplier: null,
    ncr_number: '',
    part_number: '',
    description: '',
    batch_number: '',
    batch_qty: '',
    reject_qty: '',
    shortage_qty: '',
    defect_description: '',
    grn_number: '',
    po_number: '',
    unit_price: '',
    works_order: '',
    po_line: null,
    po_line_number: '',
    grn: null,
    source_of_rejection_option: null,
    fault_group: null,
    fault_category: null,
    liability_type: 0,
    is_collection_required: null,
    is_credit_required: null,
    is_replacement_required: null,
    is_internal: false,
    assigned_to: null,
    buyer_name: '',
    is_8d_required: null,
    containment_required: false,
    containment_qty_ok: '',
    containment_qty_not_ok: '',
    containment_current_stock: '',
  });

  // Customer review 2026-07: live repeat-issue flag (same part + fault group
  // raised before) and the ERP current-stock snapshot for containment.
  const [repeatInfo, setRepeatInfo] = useState<RepeatInfo | null>(null);
  const [currentStock, setCurrentStock] = useState<number | null>(null);

  useEffect(() => {
    if (!formData.part_number.trim() || !formData.fault_group) {
      setRepeatInfo(null);
      return;
    }
    const t = setTimeout(() => {
      qualityAPI.ncrRepeatCheck({ part_number: formData.part_number.trim(), fault_group: formData.fault_group as number })
        .then(res => setRepeatInfo(res.data))
        .catch(() => setRepeatInfo(null));
    }, 400);
    return () => clearTimeout(t);
  }, [formData.part_number, formData.fault_group]);

  useEffect(() => {
    if (!formData.containment_required || !formData.part_number.trim()) {
      setCurrentStock(null);
      return;
    }
    const t = setTimeout(() => {
      qualityAPI.ncrPartStock({ part_number: formData.part_number.trim() })
        .then(res => setCurrentStock(res.data.stock_quantity))
        .catch(() => setCurrentStock(null));
    }, 400);
    return () => clearTimeout(t);
  }, [formData.containment_required, formData.part_number]);

  // Fetch suppliers and classification options
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await suppliersAPI.getProfiles({ page_size: 1000, minimal: true });
        const mapped = response.data.results.map(p => ({ id: p.company_id, name: p.company_name }));
        const unique = Array.from(new Map(mapped.map(s => [s.id, s])).values())
          .sort((a, b) => a.name.localeCompare(b.name));
        setSuppliers(unique);
      } catch (error: unknown) {
        console.error('Error fetching suppliers:', error);
        toast.error('Failed to load suppliers');
      } finally {
        setLoadingSuppliers(false);
      }
    };

    const fetchClassificationOptions = async () => {
      try {
        // Walk pages — the backend clamps page_size, so one request silently truncated.
        const [sors, fgs, fcs] = await Promise.all([
          fetchAllPages<SourceOfRejection>((page) => qualityAPI.getSourcesOfRejection({ is_active: true, page_size: 100, page })),
          fetchAllPages<FaultCodeGroup>((page) => qualityAPI.getFaultGroups({ is_active: true, page_size: 100, page })),
          fetchAllPages<FaultCodeCategory>((page) => qualityAPI.getFaultCategories({ is_active: true, page_size: 100, page })),
        ]);
        setSourcesOfRejection(sors);
        setFaultGroups(fgs);
        setFaultCategories(fcs);
      } catch (error: unknown) {
        console.error('Error fetching classification options:', error);
      }
    };

    const fetchCustomerUsers = async () => {
      if (user?.company) {
        try {
          const response = await coreAPI.getCompanyUsers(user.company);
          setCustomerUsers(response.data);
        } catch {
          console.error('Error fetching customer users');
        }
      }
    };

    fetchSuppliers();
    fetchClassificationOptions();
    fetchCustomerUsers();
  }, [user?.company]);

  const updateField = (field: keyof NCRFormData, value: string | number | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // CR-6387: clear any backend error for this field once the user edits it.
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    if (field === 'grn_number') {
      setGrnLinked(false);
      setFormData(prev => ({ ...prev, [field]: value as string, po_line: null, po_line_number: '', grn: null }));
    }
    // When fault group changes, filter categories and reset category selection
    if (field === 'fault_group') {
      const groupId = value as number | null;
      if (groupId) {
        setFilteredCategories(faultCategories.filter(c => c.fault_group === groupId));
      } else {
        setFilteredCategories([]);
      }
      setFormData(prev => ({ ...prev, fault_group: groupId, fault_category: null }));
    }
  };

  const doGRNLookup = useCallback(async (grnNumber: string, suppliersList: Array<{ id: number; name: string }>) => {
    try {
      setLookingUpGRN(true);
      // Exact match on the GRN number, and scoped to the site the user is
      // viewing. `search=` hits grn_number, part number, PO number AND batch
      // number by substring, so "96" pulled back unrelated GRNs — and the old
      // `|| grns[0]` fallback then silently linked the first of them and
      // reported success. An NCR pointed at the wrong GRN is worse than one
      // the user has to enter by hand, so a near miss is now no match.
      const response = await purchasingAPI.getGRNs({
        grn_number: grnNumber,
        page_size: 5,
        all_suppliers: true,
        ...getViewQueryParams(),
      });
      const grns = response.data.results;
      const grn = grns?.find(g => g.grn_number === grnNumber);

      if (!grn) {
        toast.error(`No GRN found with the number "${grnNumber}" in the current view.`);
        return false;
      }
      // Cast to access all fields returned by API (some not in TS type)
      const grnData = grn as unknown as Record<string, unknown>;

      const updates: Partial<NCRFormData> = {
        grn: grn.id,
        grn_number: grn.grn_number || grnNumber,
      };

      // Populate from top-level GRN fields (API returns these directly)
      if (grn.po_number) updates.po_number = grn.po_number;
      if (grn.line_number) updates.po_line_number = String(grn.line_number);
      if (grn.part_number) updates.part_number = grn.part_number;
      if (grnData['description']) updates.description = String(grnData['description']);
      if (grn.part_description) updates.description = grn.part_description;
      // NCR.unit_price is a 2dp field. PO/GRN prices carry 3, so copying one
      // straight across produced "15.000", which the serializer rejects with
      // "Ensure that there are no more than 2 decimal places" — the form
      // looked filled in correctly and simply refused to save.
      if (grn.unit_price) updates.unit_price = displayPrice(grn.unit_price, priceDecimals);
      if (grn.batch_number) updates.batch_number = grn.batch_number;

      // Set po_line FK (API may return as po_line or po_line_id)
      const poLineId = grn.po_line || (grnData['po_line_id'] as number | undefined);
      if (poLineId) {
        updates.po_line = poLineId;
        // Try to fetch PO line for additional details (supplier, etc.)
        try {
          const lineResp = await purchasingAPI.getLine(poLineId);
          const line = lineResp.data;
          if (line.po_number && !updates.po_number) updates.po_number = line.po_number;
          if (line.line_number && !updates.po_line_number) updates.po_line_number = String(line.line_number);
          if (line.part_number && !updates.part_number) updates.part_number = line.part_number;
          if (line.description && !updates.description) updates.description = line.description;
          if (line.unit_price && !updates.unit_price) updates.unit_price = displayPrice(line.unit_price, priceDecimals);
          if (line.supplier_id) updates.supplier = line.supplier_id;
        } catch {
          // PO line lookup failed, use what we have from GRN
        }
      }

      // Fallback: resolve supplier from name if not set from PO line
      if (grn.supplier_name && !updates.supplier) {
        const match = suppliersList.find(s => s.name === grn.supplier_name);
        if (match) updates.supplier = match.id;
      }

      setFormData(prev => ({ ...prev, ...updates }));
      setGrnLinked(true);
      toast.success('GRN data loaded - supplier, PO, and part details populated');
      return true;
    } catch (error: unknown) {
      console.error('Error looking up GRN:', error);
      toast.error('Failed to look up GRN');
      return false;
    } finally {
      setLookingUpGRN(false);
    }
    // Both are read inside: an empty dep list would pin the lookup to the
    // view context and precision as they were on first render, so switching
    // site and looking up again would silently query the old scope.
  }, [getViewQueryParams, priceDecimals]);

  const handleDialogLookup = async () => {
    const trimmed = grnInput.trim();
    if (!trimmed) {
      toast.error('Please enter a GRN number');
      return;
    }
    const success = await doGRNLookup(trimmed, suppliers);
    if (success) {
      setShowGrnDialog(false);
    }
  };

  const handleSkipGrn = () => {
    setShowGrnDialog(false);
  };

  const handleSubmit = async () => {
    // CR-6387: collect ALL client-side validation errors up front so each one
    // surfaces inline (red border + message), not just a toast — and so the
    // stale-error clear happens only once every check passes. Clearing at the
    // very top and then returning early left the user with a toast but no
    // inline indication of the offending field.
    const clientErrors: Record<string, string> = {};
    if (!formData.supplier) clientErrors['supplier'] = 'Please select a supplier.';
    if (!formData.source_of_rejection_option) clientErrors['source_of_rejection_option'] = 'Please select a source of rejection.';
    // Fault Group/Category are mandatory unless the admin has made them optional.
    if (!ncrFaultClassificationOptional) {
      if (!formData.fault_group) clientErrors['fault_group'] = 'Please select a fault group.';
      if (!formData.fault_category) clientErrors['fault_category'] = 'Please select a fault category.';
    }
    if (!formData.defect_description.trim()) clientErrors['defect_description'] = 'Please enter a defect description.';
    // Liability is required (marked * in the UI); 0 means no owner picked.
    if (!formData.liability_type) clientErrors['liability_type'] = 'Please select a liability.';
    if (formData.containment_required) {
      if (formData.containment_qty_ok === '') clientErrors['containment_qty_ok'] = 'Required when requesting stock containment.';
      if (formData.containment_qty_not_ok === '') clientErrors['containment_qty_not_ok'] = 'Required when requesting stock containment.';
    }
    // The required Yes/No actions are tri-state — an unselected (null) field must
    // NOT silently submit as "No". Require an explicit choice for the fields
    // actually shown for this liability owner.
    if (formData.liability_type > 0) {
      const requiredYesNo: Array<keyof NCRFormData> = ['is_8d_required', 'is_collection_required'];
      if (formData.liability_type === 1) {
        requiredYesNo.push('is_credit_required', 'is_replacement_required');
      }
      for (const key of requiredYesNo) {
        if (formData[key] === null) clientErrors[key] = 'Please choose Yes or No.';
      }
    }

    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      toast.error('Please correct the highlighted fields before submitting.');
      return;
    }

    // All client-side checks passed — clear any stale (backend) errors and submit.
    setFieldErrors({});

    try {
      setSaving(true);

      const apiData: Record<string, unknown> = {
        supplier: formData.supplier,
        defect_description: formData.defect_description,
      };

      if (formData.ncr_number) apiData['ncr_number'] = formData.ncr_number;
      if (formData.part_number) apiData['part_number'] = formData.part_number;
      if (formData.description) apiData['description'] = formData.description;
      if (formData.batch_number) apiData['batch_number'] = formData.batch_number;
      if (formData.grn_number) apiData['grn_number'] = formData.grn_number;
      if (formData.po_number) apiData['po_number'] = formData.po_number;
      if (formData.works_order) apiData['works_order'] = formData.works_order;
      if (formData.unit_price) apiData['unit_price'] = formData.unit_price;
      if (formData.po_line) apiData['po_line'] = formData.po_line;
      if (formData.grn) apiData['grn'] = formData.grn;
      apiData['source_of_rejection_option'] = formData.source_of_rejection_option;
      apiData['fault_group'] = formData.fault_group;
      apiData['fault_category'] = formData.fault_category;
      apiData['liability_type'] = formData.liability_type;
      // Tri-state selects: null (unselected) submits as false — the backend
      // BooleanFields don't accept null.
      apiData['is_8d_required'] = formData.is_8d_required ?? false;
      apiData['is_collection_required'] = formData.is_collection_required ?? false;
      apiData['is_credit_required'] = formData.is_credit_required ?? false;
      apiData['is_replacement_required'] = formData.is_replacement_required ?? false;
      apiData['is_internal'] = formData.is_internal;
      apiData['assigned_to'] = formData.assigned_to;
      if (formData.buyer_name) apiData['buyer_name'] = formData.buyer_name;

      // Left blank means nothing to report, which the model stores as 0.
      apiData['batch_qty'] = Number(formData.batch_qty) || 0;
      apiData['reject_qty'] = Number(formData.reject_qty) || 0;
      apiData['shortage_qty'] = Number(formData.shortage_qty) || 0;

      // Containment status (customer review 2026-07)
      apiData['containment_required'] = formData.containment_required;
      if (formData.containment_required) {
        apiData['containment_qty_ok'] = formData.containment_qty_ok;
        apiData['containment_qty_not_ok'] = formData.containment_qty_not_ok;
        // Prefer the ERP snapshot; fall back to the manually-entered figure
        // for tenants with no stock feed.
        if (currentStock !== null) {
          apiData['containment_current_stock'] = currentStock;
        } else if (formData.containment_current_stock !== '') {
          apiData['containment_current_stock'] = Number(formData.containment_current_stock);
        }
      }

      const response = await qualityAPI.createNCR(apiData);
      toast.success('NCR created successfully');
      navigate(`/quality/ncr/${response.data.id}/edit`);
    } catch (error: unknown) {
      console.error('Error creating NCR:', error);
      // CR-6387: surface the real reason (and highlight offending fields)
      // instead of a blanket "Failed to create NCR".
      const { message, fields } = extractNcrError(error);
      setFieldErrors(fields);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // Show loading while checking tab settings
  if (!tabSettingsLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // If NCRs tab is disabled, show access denied (redirect should have happened)
  if (!tabVisibility.ncrs) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">This section is not available. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  const handleContainmentChange = (
    field: 'containment_required' | 'containment_qty_ok' | 'containment_qty_not_ok' | 'containment_current_stock',
    value: boolean | string,
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // CR-6387: clear a containment field's backend error as it's edited.
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <NCRGrnLinkDialog
        open={showGrnDialog}
        onOpenChange={setShowGrnDialog}
        value={grnInput}
        onValueChange={setGrnInput}
        lookingUp={lookingUpGRN}
        onLookup={handleDialogLookup}
        onSkip={handleSkipGrn}
      />

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/quality')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Create New NCR</h1>
          <p className="text-muted-foreground">Create a new Non-Conformance Report</p>
        </div>
      </div>

      <NCRDetailsForm
        formData={formData}
        fieldErrors={fieldErrors}
        updateField={updateField}
        onContainmentChange={handleContainmentChange}
        suppliers={suppliers}
        loadingSuppliers={loadingSuppliers}
        grnLinked={grnLinked}
        lookingUpGRN={lookingUpGRN}
        doGRNLookup={doGRNLookup}
        sourcesOfRejection={sourcesOfRejection}
        faultGroups={faultGroups}
        filteredCategories={filteredCategories}
        customerUsers={customerUsers}
        repeatInfo={repeatInfo}
        currentStock={currentStock}
        saving={saving}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/quality')}
      />
    </div>
  );
}
